import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { usePingUnit, useUnitControlProgram } from '@apps-common/apis/portal/control'
import { AccountChangeModal } from '@apps-common/apps/portal/pages/units/modals/AccountChangeModal'
import {
  AccountStatus,
  ActivationStatus,
  ConnectivityStatus,
  PingButton,
  SignalStatus
} from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { VertSpacerMd, VertSpacerSm } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { ModalBody, ModalContainer } from '@apps-common/components/modals/modal'
import { useHistoryModal } from '@apps-common/components/modals/unit-history'
import { MessageProgramControl } from '@apps-common/config/app-messages'
import { CONTROL_STATE_ON, ControlProgramCommand } from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { unitIsActive, unitIsCancelled, unitIsInactive } from '@apps-common/helpers/unit-helpers'
import { toastError, toastSuccess } from '@apps-common/lib/toast'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'
import {
  DeviceStatus,
  OUTPUT_CLOSED,
  OUTPUT_OPEN,
  SENSOR_HIGH_ALARM,
  SENSOR_HIGH_RESTORE,
  SENSOR_LOW_ALARM,
  SENSOR_LOW_RESTORE
} from '@apps-common/utils/device-status'

export interface IStatusInfoModal {
  unit: IUnitGenericDetailsModel
}

export const StatusInfoModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IStatusInfoModal
}) => {
  const { unit } = componentProps
  const [status, setStatus] = useState<DeviceStatus | undefined>()
  const { mutate: restartDevice } = useUnitControlProgram(
    false,
    () => toastSuccess(MessageProgramControl.RestartSuccess),
    () => toastError(MessageProgramControl.RestartFailure)
  )
  const { open: openHistory } = useHistoryModal({ type: 'program', name: unit.siteName || unit.accountNo, id: 0, unitId: unit.unitId })
  const isCancelled = unitIsCancelled(unit)

  const { isLoading, isFetching, isRefetching, isError, data, refetch } = usePingUnit({
    config: {
      enabled: !isCancelled,
      retry: false,
      staleTime: 1000 * 60 * 60 * 24
    },
    model: {
      unitId: unit.unitId,
      triggerType: '',
      onTime: 0,
      commands: []
    }
  })

  useEffect(() => {
    if (data) {
      if (!data.pingResult) {
        refetch()
      } else {
        const statusObj = new DeviceStatus(unit.accountNo, unit.productId, unit.product, unit.imageUrl, data)

        setStatus(statusObj)
      }
    }
  }, [data])

  const isInactive = unitIsInactive(unit)
  const isActive = unitIsActive(unit)
  const loading = !unit || isLoading || isFetching || isRefetching

  return (
    <ModalContainer
      innerClasses='!bg-transparent !border-none !max-w-[800px] p-0 !h-[100vh]'
      containerClasses='!items-center !justify-center'
      onDismiss={onDismiss}
    >
      <ModalBody classes='!pt-[40px] !flex !items-center !justify-center'>
        <div className='bg-surface-2 rounded-[35px] !h-auto !min-h-0 !p-4 sm:!p-6 sm:min-h-[400px] min-w-0 sm:min-w-[600px] md:min-w-[700px] border border-border'>
          <VertSpacerSm />
          <div className='flex flex-col gap-[15px] md:flex-row mx-auto'>
            <div className='grow flex flex-col'>
              <div className='relative flex items-center justify-between sm:px-[10px] pb-4'>
                <div className='flex items-center justify-between sm:gap-[6px]'>
                  {/* <ProductStatus loading={loading} data={status?.model || { imageUrl: unit?.imageUrl || '', name: unit?.product || '' }} /> */}
                  <AccountStatus loading={loading} isInactive={isInactive} data={status?.accountNo || unit?.accountNo} />
                  <ActivationStatus loading={loading} isInactive={isInactive} isActive={isActive} isCancelled={isCancelled} />
                  <ConnectivityStatus loading={loading} data={status?.online} />
                  <SignalStatus loading={loading} data={status?.signal} />
                </div>
                <div className='flex items-center justify-center p-[5px] gap-[5px] bg-surface border border-border rounded-full'>
                  <PingButton
                    refetch={refetch}
                    requestModel={{
                      unitId: unit?.unitId || '',
                      triggerType: '',
                      onTime: 0,
                      commands: []
                    }}
                    disabled={isCancelled}
                  />
                  <ButtonRoundSm type='button' size='lg' className='action-brand' callback={() => onDismiss(null, ModalDismiss.Cancel)}>
                    <commonIcons.close className='!text-[1.5rem]' />
                  </ButtonRoundSm>
                </div>
              </div>
              <div className='flex flex-col sm:flex-row items-stretch justify-between gap-4 grow'>
                {loading && (
                  <>
                    <div className='w-full min-w-[260px] min-h-[140px] grow bg-skeleton rounded-[25px] px-[15px] py-[12px] animate-pulse flex flex-col gap-2'>
                      <div className='flex items-center justify-between'>
                        <div className='w-[50%] h-[24px] rounded-full bg-skeleton' />
                        <div className='w-[28px] h-[24px] rounded-full bg-skeleton' />
                      </div>
                      <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                      <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                      <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                    </div>
                    <div className='w-full min-w-[260px] grow bg-skeleton rounded-[25px] px-[15px] py-[12px] animate-pulse flex flex-col gap-2'>
                      <div className='flex items-center justify-between'>
                        <div className='w-[50%] h-[24px] rounded-full bg-skeleton' />
                        <div className='w-[28px] h-[24px] rounded-full bg-skeleton' />
                      </div>
                      <div className='w-full h-[28px] rounded-full bg-skeleton-hi' />
                    </div>
                  </>
                )}

                {!loading && (
                  <>
                    <div
                      className={classNames(
                        'w-full bg-surface border border-border rounded-[25px] px-[5px] py-[7.5px]',
                        isRefetching && 'opacity-40'
                      )}
                    >
                      <div className='text-text jost text-[0.8em] px-[15px] pt-[10px] flex items-center justify-between'>
                        Outputs <commonIcons.outputSwitch className='text-[1.3em]' />
                      </div>
                      <VertSpacerMd />
                      <div className='text-text-muted jost text-[0.75em] px-[10px] pb-[5px]'>
                        {status?.outputs &&
                          status?.outputs?.length > 0 &&
                          status?.outputs?.map((o, i) => (
                            <div
                              key={`unit-status-output-${i}`}
                              className={classNames(
                                'flex items-center justify-between py-[5px] px-[10px] rounded-[10px]',
                                i % 2 === 0 && 'bg-surface-2'
                              )}
                            >
                              <div className='jost-bold'>{o.index + 1}</div>
                              <div
                                className={classNames(
                                  'flex items-center justify-between uppercase px-[6px] rounded-[8px] w-[52px]',
                                  o.state === OUTPUT_OPEN && 'text-on-error bg-error',
                                  o.state === OUTPUT_CLOSED && 'text-on-success bg-success'
                                )}
                              >
                                {o.state} {o.state === OUTPUT_OPEN && <commonIcons.powerOffAlt className='text-[1.1em]' />}
                                {o.state === OUTPUT_CLOSED && <commonIcons.powerAlt className='text-[1.1em]' />}
                              </div>
                            </div>
                          ))}
                        {!loading && (!status?.outputs || status?.outputs?.length === 0) && <div className='ml-[5px]'>No output data</div>}
                      </div>
                    </div>

                    <div
                      className={classNames(
                        'w-full bg-surface border border-border rounded-[25px] px-[5px] py-[7.5px]',
                        isRefetching && 'opacity-40'
                      )}
                    >
                      <div className='text-text jost text-[0.8em] px-[15px] pt-[10px] flex items-center justify-between'>
                        Sensors <commonIcons.temp className='text-[1.45em]' />
                      </div>
                      <VertSpacerMd />
                      <div className='text-text-muted jost text-[0.8em] px-[10px] pb-[5px]'>
                        {status?.sensors &&
                          status?.sensors?.length > 0 &&
                          status?.sensors?.map((s, i) => (
                            <div
                              key={`unit-status-sensor-${i}`}
                              className={classNames(
                                'flex items-center justify-between py-[5px] px-[10px] rounded-[10px]',
                                i % 2 === 0 && 'bg-surface-2'
                              )}
                            >
                              <div className='uppercase'>
                                {s.index + 1}: <span className='text-[0.9em]'>{s?.status?.status}</span>
                              </div>
                              <div className='flex items-center justify-center gap-[10px]'>
                                <div className='flex items-center justify-center'>
                                  <commonIcons.arrowRight className='rotate-[90deg] text-[1.1em]' />
                                  <div
                                    className={classNames(
                                      'w-[12px] h-[12px] rounded-[6px]',
                                      s?.status?.lowAlarm === SENSOR_LOW_ALARM && 'bg-error',
                                      s?.status?.lowAlarm === SENSOR_LOW_RESTORE && 'bg-success'
                                    )}
                                  />
                                </div>
                                <div className='flex items-center justify-center'>
                                  <commonIcons.arrowRight className='rotate-[-90deg] text-[1.1em]' />
                                  <div
                                    className={classNames(
                                      'w-[12px] h-[12px] rounded-[6px]',
                                      s?.status?.lowAlarm === SENSOR_HIGH_ALARM && 'bg-error',
                                      s?.status?.highAlarm === SENSOR_HIGH_RESTORE && 'bg-success'
                                    )}
                                  />
                                </div>
                                <div className='flex items-center justify-center'>
                                  {s.value}
                                  <commonIcons.tempCelsius className='text-[1.35em]' />
                                </div>
                              </div>
                            </div>
                          ))}
                        {!loading && (!status?.sensors || status?.sensors?.length === 0) && <div className='ml-[5px]'>No sensor data</div>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className='flex md:flex-col items-end justify-around md:justify-start gap-[15px] h-full'>
              <ModalButton
                component={AccountChangeModal}
                disabled={loading || isFetching || !data?.pingResult}
                componentProps={{ unitId: unit.unitId }}
                classes='w-full'
                onClickCallback={() => onDismiss(null, 'cancel')}
                backdropDismiss
              >
                <div className='w-full bg-surface border border-border text-text rounded-[25px] px-[20px] md:px-[25px] py-[15px] flex flex-col items-center justify-start gap-[10px]'>
                  <commonIcons.processor className='text-[1.5em]' />
                  <div className='jost text-[0.7em] uppercase grow text-center'>Account</div>
                </div>
              </ModalButton>

              <ModalButton
                component={ConfirmModal}
                disabled={loading || isRefetching || !data?.pingResult}
                componentProps={{
                  text: 'Remote restart',
                  description: 'Confirm you would like to perform a remote restart on this device?',
                  action: ConfirmModalAction.Warning
                }}
                classes='w-full'
                onClickCallback={() => onDismiss(null, 'cancel')}
                confirmCallback={() => {
                  restartDevice({
                    model: {
                      unitId: unit?.unitId,
                      triggerType: '',
                      onTime: 0,
                      commands: [
                        {
                          id: 0,
                          unitControlId: 0,
                          commandState: CONTROL_STATE_ON,
                          command: ControlProgramCommand.Restart,
                          value: ''
                        }
                      ]
                    }
                  })
                }}
                backdropDismiss
              >
                <div className='w-full bg-surface border border-border text-text rounded-[25px] px-[20px] md:px-[25px] py-[15px] flex flex-col items-center justify-center gap-[10px]'>
                  <commonIcons.refresh className='text-[1.5em]' />
                  <div className='jost text-[0.7em] uppercase grow text-center'>Restart</div>
                </div>
              </ModalButton>
              <button
                type='button'
                disabled={false}
                className='disabled:opacity-30 w-full'
                onClick={() => {
                  onDismiss(null, ModalDismiss.Cancel)
                  openHistory()
                }}
              >
                <div className='w-full bg-surface border border-border text-text rounded-[25px] px-[20px] md:px-[25px] py-[15px] flex flex-col items-center justify-center gap-[10px]'>
                  <commonIcons.history className='text-[1.75em]' />
                  <div className='jost text-[0.7em] uppercase text-center'>History</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </ModalBody>
    </ModalContainer>
  )
}
